/**
 * Email Configuration Tooltips
 *
 * Organized by email server type with common tooltips shared across all types.
 * Usage: import { EMAIL_TOOLTIPS, getTooltip } from '../constants/tooltips/emailTooltips';
 */

// Tooltips specific to each email server type
const EMAIL_TYPE_TOOLTIPS = {
    SMTP: {
        host: "Enter the SMTP server hostname or IP address.\nExample: mail.example.com or 192.168.1.100",
        port: "Valid port range is 1-65535.\nCommon values:\n• 587 for STARTTLS\n• 465 for SSL/TLS\n• 25 for unencrypted (not recommended)",
        username: "SMTP authentication username.\nUsually an email address or account name.\nLeave blank if your server doesn't require authentication.",
        password: "SMTP authentication password.\nLeave blank if your server doesn't require authentication.",
    },
    Office365: {
        host: "For Office365, use: smtp.office365.com",
        port: "Valid port range is 1-65535.\nCommon values:\n• 587 for STARTTLS\n• 465 for SSL/TLS\n• 25 for unencrypted (not recommended)\nFor Office365 use port 587.",
        username: "Your Office365 email address.\nThis is the account that will be used to authenticate and send emails.",
        password: "Your Office365 account password.\nNote: If MFA is enabled, you may need to create an App Password.",
    },
    MicrosoftGraph: {
        tenantId: "Found in Azure Portal:\nAzure Active Directory > Overview > Tenant ID\nFormat: xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx",
        clientId: "Found in Azure Portal:\nApp registrations > Your App > Application (client) ID\nFormat: xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx",
        clientSecret: "Found in Azure Portal:\nApp registrations > Your App > Certificates & secrets\nCreate a new client secret and copy the Value (not the Secret ID).\nNote: The secret value is only shown once when created.",
        username: "The email address that will be used to send emails.\nThis user must have a mailbox and the app must have Mail.Send permission.",
    },
    SendGrid: {
        apiKey: "Found in SendGrid:\nSettings > API Keys > Create API Key\nRestricted access with Mail Send permission is sufficient.\nNote: The key value is only shown once when created.",
    },
};

// Tooltips that are the same regardless of email type
const COMMON_TOOLTIPS = {
    configName: "A descriptive name to identify this email configuration.\nExample: Production SMTP, Marketing Emails, etc.",
    serverType: "SMTP: Traditional email protocol. Works with most mail servers.\n\nMicrosoft Graph: Modern API for Microsoft 365. Requires Azure AD app registration. Recommended for Microsoft 365.\n\nOffice365 (Basic Auth): Legacy authentication. Microsoft is deprecating this - use Graph instead if possible.\n\nSendGrid: Cloud email delivery service. Requires a SendGrid account and API key.",
    folder: "The Sage X3 folder this email configuration will be used for.\nMust match the folder name exactly as configured in Sage X3 Settings.",
    isDefault: "When enabled, this configuration will be used as the default for the selected folder.\nOnly one configuration can be default per folder.\nSetting this will remove the default flag from other configurations for the same folder.",
    isSystemNotification: "When enabled, this configuration sends system-level notifications — licence expiry alerts and other system-wide issues — to its list of admin recipients.\nOnly one configuration can be the System Notification sender.\nIt can still be used for normal document emails at the same time.",
    adminRecipients: "The admin recipients for system notifications sent by this configuration.\nAdd one or more email addresses — select an admin user or type any address.\nThese addresses replace the single Admin Address for this configuration.",
    fromAddress: "The email address that appears in the 'From' field.\nFormats accepted:\n• info@domain.com\n• Display Name <info@domain.com>\nMust be a valid email the server allows you to send from.",
    replyTo: "Optional: The email address recipients will reply to.\nIf left blank, replies go to the From Address.\nUseful for routing replies to a different mailbox.",
    adminAddress: "Email address for receiving test emails and system notifications.\nTest emails from this configuration will be sent to this address.",
};

// Security-related tooltips (SMTP only)
const SECURITY_TOOLTIPS = {
    secure: "Enable for implicit TLS/SSL connections.\nTypically used with port 465.\nThe connection starts encrypted from the beginning.",
    ignoreTLS: "When enabled, the server's STARTTLS extension will be ignored.\nUse only for testing or if TLS is causing issues.\nNot recommended for production.",
    requireTLS: "Requires STARTTLS upgrade after connecting.\nTypically used with port 587.\nFails if the server doesn't support STARTTLS.",
    tlsServerName: "Optional: Override the server name used for TLS certificate validation.\nUseful when connecting via IP address or when the certificate CN doesn't match the hostname.",
    bypassProxy: "When enabled, emails will be sent directly without going through the configured HTTP proxy.\nUse this for local or internal SMTP servers (e.g. hMailServer) that are not reachable via the proxy.",
};

/**
 * Get a tooltip for a specific field and email type
 * @param {string} field - The field name (e.g., 'host', 'port', 'username')
 * @param {string} emailType - The email type (e.g., 'SMTP', 'Office365', 'MicrosoftGraph')
 * @returns {string} The tooltip text
 */
export function getTooltip(field, emailType = 'SMTP') {
    // Check common tooltips first
    if (COMMON_TOOLTIPS[field]) {
        return COMMON_TOOLTIPS[field];
    }

    // Check security tooltips
    if (SECURITY_TOOLTIPS[field]) {
        return SECURITY_TOOLTIPS[field];
    }

    // Check type-specific tooltips
    const typeTooltips = EMAIL_TYPE_TOOLTIPS[emailType] || EMAIL_TYPE_TOOLTIPS.SMTP;
    if (typeTooltips[field]) {
        return typeTooltips[field];
    }

    // Fallback to SMTP tooltips if the type doesn't have this field
    if (EMAIL_TYPE_TOOLTIPS.SMTP[field]) {
        return EMAIL_TYPE_TOOLTIPS.SMTP[field];
    }

    return '';
}

/**
 * Get all tooltips for a specific email type (merged with common tooltips)
 * @param {string} emailType - The email type
 * @returns {Object} Object containing all tooltips for the type
 */
export function getTooltipsForType(emailType = 'SMTP') {
    return {
        ...COMMON_TOOLTIPS,
        ...SECURITY_TOOLTIPS,
        ...(EMAIL_TYPE_TOOLTIPS[emailType] || EMAIL_TYPE_TOOLTIPS.SMTP),
    };
}

// Export all tooltip objects for direct access if needed
export const EMAIL_TOOLTIPS = {
    common: COMMON_TOOLTIPS,
    security: SECURITY_TOOLTIPS,
    byType: EMAIL_TYPE_TOOLTIPS,
};

export default EMAIL_TOOLTIPS;
